<template>
  <div class="modal-mask">
    <div class="modal-wrapper">
      <div class="modal-container" :style="($store.state.currentLocation?.state == 'TAS') ? { width: '80%' } : { width: '50%'}">
        <div class="modal-header">
          <slot name="header">
            <h3 class="form-header">{{ mode === 'Create' ? 'Create Requisition' : 'Supply Requisition' }}</h3>
            <button @click="closeModal" class="red-close-button">
              <i class="pi pi-times" style="padding: 0"></i>
            </button>
          </slot>
        </div>

        <div class="modal-body">
          <slot name="body">
            <form class="action-modal" @submit.prevent>
              <div class="form-section-container" :style="($store.state.currentLocation?.state == 'TAS') ? { gridTemplateColumns: '2fr 9fr', width: '100%' } : { } ">

                <!-- LEFT COLUMN -->
                <div class="left-form-section-panel">
                  <!-- Create Mode Location & Notes -->
                  <div v-if="mode === 'Create'">
                    <!-- Stock always flows source -> destination. Whichever end the user is
                         standing at is pre-filled, but both remain selectable. -->
                    <div style="margin-bottom: 10px;">
                      <p style="text-align: left; margin: 0 0 3px 0; font-size: 0.9em;">
                        <b>Source Location</b> <span style="color: #666;">(supplying)</span>
                      </p>
                      <Dropdown
                        v-model="sourceSearch"
                        placeholder="Type a location to request from"
                        ref="sourceLocationRef"
                        @change="sourceSelect"
                        @keyup="debouncedSourceSearch"
                        @focus="loadSourceLocations"
                        :loading="loadingSource"
                        :options="sourceOptions"
                        optionLabel="name"
                        :editable="true"
                        style="width: 100%; display: flex;"
                      />
                    </div>

                    <div style="margin-bottom: 10px;">
                      <p style="text-align: left; margin: 0 0 3px 0; font-size: 0.9em;">
                        <b>Destination Location</b> <span style="color: #666;">(requesting)</span>
                      </p>
                      <Dropdown
                        v-model="destinationSearch"
                        placeholder="Type a location to deliver to"
                        ref="destinationLocationRef"
                        @change="destinationSelect"
                        @keyup="debouncedDestinationSearch"
                        @focus="loadDestinationLocations"
                        :loading="loadingDestination"
                        :options="destinationOptions"
                        optionLabel="name"
                        :editable="true"
                        style="width: 100%; display: flex;"
                      />
                    </div>
                  </div>

                  <!-- Supply Mode Info -->
                  <div v-else style="text-align: left; margin-bottom: 1em; font-size: 0.9em; line-height: 1.4em;">
                    <p><b>Supplying Location:</b> {{ requisitionData.from ? requisitionData.from.name : 'Unknown' }}</p>
                    <p><b>Requesting Location:</b> {{ requisitionData.to ? requisitionData.to.name : 'Unknown' }}</p>
                    <p><b>Requested By:</b> {{ getPersonName(requisitionData.requested_by) }}</p>
                    <p><b>Notes:</b> {{ requisitionData.notes || 'None' }}</p>
                  </div>

                  <div>
                    <p style="text-align: left">Notes <span v-if="$store.state.currentLocation?.state == 'TAS'" style="font-size: 10px; color:red">*</span></p>
                    <textarea
                      name="notes"
                      id="note-modal"
                      cols="44"
                      rows="12"
                      placeholder="Please enter notes here..."
                      style="border-top-left-radius: 0px; border-top-right-radius: 0px; width: 100%;"
                      v-model="notes"
                      :disabled="mode === 'Supply'"
                    ></textarea>
                  </div>
                </div>

                <!-- RIGHT COLUMN -->
                <div class="right-form-section-panel" style="display: flex; flex-direction: column;">

                  <!-- Create Mode: Add items flow -->
                  <div v-if="mode === 'Create'">
                    <!-- The medication list is the source location's register, so there is
                         nothing to search until a source is chosen. -->
                    <div v-if="!sourceLocation" class="resident-help-note">
                      Select a source location to choose medications from its stock.
                    </div>

                    <template v-else>
                      <!-- Search Resident button (if movement default is 2 and no patient is selected) -->
                      <div v-if="($store.state.currentLocation?.facility?.medication_movement_default === 2) && !patient" style="margin-bottom: 10px;">
                        <p class="submit-button blue-button" @click="displayPatientSearchModal = true" style="display: inline-block; width: auto; padding: 10px 20px; cursor: pointer;">
                          Search {{ $resident({ capitalize: true }) }}
                        </p>
                      </div>

                      <!-- Select Imprest / Resident Med selector buttons block.
                           Hidden once the first item is added: a requisition is either all imprest
                           or all resident medication, and the backend rejects a mix. -->
                      <div v-if="!requestedDrugs.length && (!$store.state.currentLocation?.facility?.medication_movement_default || $store.state.currentLocation?.facility?.medication_movement_default === 0) && (!imprest_pom || (imprest_pom === 'pom' && !patient))" style="margin-bottom: 10px;">
                        <p style="margin: 0; padding: 0; padding-bottom: 10px; font-size: 0.95em; text-align: left;">
                          Please select whether this is imprest stock or {{ $resident({ lowercase: true }) }} medication.
                        </p>
                        <div class="pom-imprest-select">
                          <p
                            @click="() => { imprest_pom = 'imprest'; selectedStock = null; searchDrugs(true); }"
                            class="pom-imprest-choice-button"
                            :class="[imprest_pom == 'imprest' ? 'active-select-filter' : '']"
                          >
                            Imprest Stock
                          </p>
                          <p
                            @click="() => { imprest_pom = 'pom'; displayPatientSearchModal = true; selectedStock = null; }"
                            class="pom-imprest-choice-button"
                            :class="[imprest_pom == 'pom' ? 'active-select-filter' : '']"
                          >
                            {{ $resident({ capitalize: true }) }} Medication
                          </p>
                        </div>
                      </div>

                      <!-- Resident help note -->
                      <div v-if="imprest_pom === 'pom' && !patient" class="resident-help-note">
                        Before requesting stock, please choose the {{ $resident({ lowercase: true }) }} it belongs to.
                      </div>

                      <!-- Selected Patient Details -->
                      <div v-if="imprest_pom === 'pom' && patient" style="background-color: #f7f7f7; padding: 10px; border-radius: 4px; margin-bottom: 10px; font-size: 0.95em; text-align: left;">
                        <p style="margin: 0;"><b>Patient:</b> {{ patient.personal_information.first_name }} {{ patient.personal_information.last_name }}</p>
                        <p style="margin: 5px 0 0 0; color: blue; cursor: pointer; text-decoration: underline;" @click="displayPatientSearchModal = true">Change Patient</p>
                      </div>

                      <!-- No prescriber on the create flow: CreateRequisitionRequest has no field
                           for one, so anything entered here was collected and dropped. The
                           prescriber is captured per item at supply instead, which is where the
                           backend reads it. -->

                      <!-- Add Medication -->
                      <div class="right-form-section-drug-entry" v-if="imprest_pom === 'imprest' || (imprest_pom === 'pom' && patient)">
                        <div style="display: flex; align-items: center; width: 100%;">
                          <Dropdown
                            v-model="selectedStock"
                            ref="med"
                            @keyup="searchDrugs"
                            @focus="onDrugFocus"
                            :loading="loadingDrugs"
                            :options="filteredDrugList"
                            optionLabel="full_name"
                            :editable="true"
                            :emptyMessage="drugPickerEmptyMessage"
                            style="width: 100% !important; display: flex;"
                            placeholder="Select Medication"
                          />
                        </div>
                        <input
                          v-model="qty"
                          type="number"
                          min="1"
                          placeholder="Enter qty"
                          style="position: relative; width: 20%;"
                        />
                        <p @click="addRequestedDrug" class="submit-button blue-button">Add</p>
                      </div>

                      <!-- Requesting more than the source currently holds is allowed - the stock may
                           be on order or arriving - but the requester should see it before signing. -->
                      <p v-if="qtyExceedsHeld" style="text-align: left; font-size: 0.85em; color: #b06d00; margin: 0 0 10px 0;">
                        {{ sourceLocation.name }} currently holds {{ selectedStockQtyLeft }}. The pharmacy will confirm what it can supply.
                      </p>

                      <!-- Created Requisition Items Table -->
                      <div class="right-form-section-drug-container" v-if="requestedDrugs.length > 0">
                        <table style="border-collapse: collapse; width: 100%;">
                          <tr v-for="(item, idx) in requestedDrugs" :key="idx" class="drug-entry-transfer">
                            <td style="width: 45%;">
                              <p>{{ item.stock.slice(0, 1).toUpperCase() + item.stock.slice(1) }}</p>
                            </td>
                            <td>
                              <p>{{ item.qty }}</p>
                            </td>
                            <td>
                              <p>{{ item.patient ? (item.patient.personal_information.first_name + ' ' + item.patient.personal_information.last_name) : 'Imprest' }}</p>
                            </td>
                            <td>
                              <p v-if="item.qty > item.qty_left" style="color: #b06d00; font-size: 0.8em;" v-tooltip.top="'Requested ' + item.qty + ', source holds ' + item.qty_left">
                                Shortfall
                              </p>
                            </td>
                            <td>
                              <div>
                                <p @click="removeDrug(idx)" class="red-close-button small-button">
                                  <i class="pi pi-times" style="padding: 0"></i>
                                </p>
                              </div>
                            </td>
                          </tr>
                        </table>
                      </div>
                    </template>
                  </div>

                  <!-- Supply Mode: confirm what was requested -->
                  <div v-else>
                    <p style="text-align: left; font-weight: bold; margin-bottom: 4px;">Requested Items:</p>
                    <!-- The pharmacy supplies the requisition as it was requested. Items cannot be
                         added, removed or re-quantified here - that would overwrite the record of
                         what the ward asked for. A requisition that cannot be filled as written is
                         cancelled and a corrected one raised. -->
                    <p style="text-align: left; font-size: 0.85em; color: #666; margin: 0 0 8px 0;">
                      All items below will be supplied as requested. If this requisition cannot be
                      filled as written, cancel it and ask for a corrected one.
                    </p>
                    <div style="max-height: 320px; overflow-y: auto; padding-right: 5px;">
                      <div v-for="(item, idx) in supplyItems" :key="idx" style="background-color: #f7f7f7; border: 1px solid #eee; border-radius: 4px; padding: 10px; margin-bottom: 10px; display: flex; flex-direction: column; gap: 5px; position: relative;">

                        <div style="display: flex; justify-content: space-between; align-items: center; font-weight: bold; font-size: 0.95em; gap: 10px;">
                          <div style="display: flex; align-items: center; gap: 8px; text-align: left;">
                            <span>{{ item.alias ? item.alias.name : 'Unknown' }}</span>
                          </div>

                          <div style="display: flex; align-items: center; gap: 12px;">
                            <span style="font-weight: normal; font-size: 0.85em; color: #666;">Qty: <b>{{ item.quantity }}</b></span>
                            <span v-if="item.stock_item" style="font-size: 0.85em; color: #666; font-weight: normal;">In stock: {{ item.stock_item.stock_level }}</span>
                            <span style="font-size: 0.85em; color: #555; font-weight: normal;">{{ $resident({ capitalize: true }) }}: {{ item.patient ? getPersonName(item.patient) : 'Imprest' }}</span>
                          </div>
                        </div>

                        <p v-if="item.stock_item && Number(item.quantity) > Number(item.stock_item.stock_level)" style="text-align: left; margin: 0; font-size: 0.8em; color: #b06d00;">
                          More was requested than this location currently holds.
                        </p>

                        <!-- Script details for patient own S8 -->
                        <div v-if="item.patient" style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-top: 5px; text-align: left; position: relative;">
                          <div>
                            <label style="font-size: 0.8em; color: #555; display: block; margin-bottom: 2px;">Script Number</label>
                            <input v-model="item.script_number" placeholder="Enter Script #" style="width: 100%; border: 1px solid #ccc; padding: 4px 8px; border-radius: 4px; font-size: 0.85em;" />
                          </div>
                          <div>
                            <label style="font-size: 0.8em; color: #555; display: block; margin-bottom: 2px;">Search Prescriber</label>
                            <input
                              v-model="item.gpSearchQuery"
                              @keyup="item.debouncedGpSearch()"
                              @keyup.enter="item.debouncedGpSearch.clear(), supplyGpSearch(item)"
                              placeholder="Prescriber name..."
                              style="width: 100%; border: 1px solid #ccc; padding: 4px 8px; border-radius: 4px; font-size: 0.85em;"
                            />
                            <div v-if="item.prescriberResults && item.prescriberResults.length" style="position: absolute; left: 0; right: 0; background: white; border: 1px solid #ccc; z-index: 15; max-height: 100px; overflow-y: scroll;">
                              <p
                                v-for="(gp, gpIdx) in item.prescriberResults"
                                :key="gpIdx"
                                @click="selectSupplyPrescriber(item, gp)"
                                style="padding: 4px; margin: 0; cursor: pointer; font-size: 0.8em; border-bottom: 1px solid #eee;"
                              >
                                {{ gp.first_name }} {{ gp.last_name }}
                              </p>
                            </div>
                            <span v-if="item.selectedPrescriber" style="font-size: 0.8em; display: block; color: green; margin-top: 2px;">Selected: Dr. {{ item.selectedPrescriber.first_name }} {{ item.selectedPrescriber.last_name }}</span>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </form>
          </slot>
        </div>

        <p style="color: red; font-size: 1em; margin: 10px 20px 0 20px;" v-if="errorMessage">{{ errorMessage }}</p>

        <!-- getRequisitions only returns requisitions where the current location is one end, so a
             requisition between two other locations will not appear in this user's own list. -->
        <div v-if="displayCrossLocationWarning" style="margin: 10px 20px 0 20px; padding: 10px; border: 1px solid #e0b25c; background-color: #fdf6e7; border-radius: 4px; text-align: left; font-size: 0.9em;">
          <p style="margin: 0 0 8px 0;">
            This requisition is between <b>{{ sourceLocation.name }}</b> and <b>{{ destinationLocation.name }}</b>.
            It will appear in the requisition lists at those two locations, not at
            <b>{{ $store.state.currentLocation.name }}</b>.
          </p>
          <p style="margin: 0;">
            <span class="blue-button" style="cursor: pointer; padding: 4px 10px; border-radius: 4px;" @click="confirmCrossLocation">Continue anyway</span>
            <span style="cursor: pointer; margin-left: 10px; text-decoration: underline;" @click="displayCrossLocationWarning = false">Go back</span>
          </p>
        </div>

        <div class="modal-footer" style="display: flex; justify-content: flex-end; padding: 5px 20px;">
          <slot name="footer">
            <p class="regular-button error-button" style="color: white; margin-right: 10px;" @click="closeModal">Cancel</p>
            <p class="regular-button green-button" @click="submitRequisition">{{ mode === 'Create' ? 'Create' : 'Supply' }} Requisition</p>
          </slot>
        </div>
      </div>
    </div>

    <PatientSearchModal @close="closePatientSearch" v-if="displayPatientSearchModal" @patient="getPatient" />
    <SignatureModal
      v-if="displaySignatureModal"
      @close="closeSignature"
      @isSuccessful="isSuccessful"
      :saveContext="saveContext"
      :payload="submitPayload"
      :endpoint="mode === 'Create' ? '/create-requisition' : '/supply-requisition-items'"
    />
  </div>
</template>

<script>
import PatientSearchModal from '@/components/modals/PatientSearchModal.vue';
import SignatureModal from '@/components/modals/SignatureModal.vue';
import axios from 'axios';
import { isPickerEmpty, pickerSearchTerm, stockPickerEmptyMessage } from '@/services/pickers';
import { debounce } from 'debounce';
import { decorateStockItems } from '@/services/stockItemDisplay';
import { filterMedicationList } from '@/services/FilterMedicationList';

export default {
  props: ['mode', 'requisitionData'],
  components: {
    PatientSearchModal,
    SignatureModal,
  },
  data() {
    return {
      displayPatientSearchModal: false,
      displaySignatureModal: false,
      // Each side of the movement gets its own search model, selected object, options array and
      // loading flag. They cannot share an options array - searching one would repopulate the other.
      sourceSearch: '',
      sourceLocation: null,
      sourceOptions: [],
      loadingSource: false,
      destinationSearch: '',
      destinationLocation: null,
      destinationOptions: [],
      loadingDestination: false,
      loadingDrugs: false,
      imprest_pom: '',
      defaultApplied: false,
      patient: null,
      notes: '',
      qty: null,
      selectedStock: null,
      drugList: [],
      requestedDrugs: [],
      errorMessage: '',
      submitPayload: {},
      saveContext: {},
      supplyItems: [],
      showInterval: null,
      displayCrossLocationWarning: false,
      crossLocationAcknowledged: false,
    }
  },
  computed: {
    // The quantity being entered against the currently selected stock item, compared with what the
    // source register holds. Only used to warn - it never blocks.
    selectedStockQtyLeft() {
      if (!this.selectedStock || typeof this.selectedStock !== 'object') return null;
      return this.selectedStock.qty_left;
    },
    qtyExceedsHeld() {
      if (this.selectedStockQtyLeft === null || this.selectedStockQtyLeft === undefined) return false;
      if (!this.qty) return false;
      return Number(this.qty) > Number(this.selectedStockQtyLeft);
    },
    // Resident medication is loaded in one go by loadPatientMeds(), so this dropdown has no
    // server side search behind it and searchDrugs() returns early for it. Same treatment as the
    // Adjustment, Destruction, Lost/Damaged and Transfer modals.
    filteredDrugList() {
      if (this.imprest_pom === 'pom' && this.patient) {
        return filterMedicationList(this.drugList, this.selectedStock);
      }
      return this.drugList;
    },
    // The one picker serves two sources, so the "nothing to offer" message has to name the right
    // one: a resident's own supply, or the stock the supplying location holds - which is not this
    // user's own location, so it must not read as "here".
    drugPickerEmptyMessage() {
      if (this.imprest_pom === 'pom' && this.patient) {
        return stockPickerEmptyMessage(this.selectedStock, `No medications held for this ${this.$resident({ lowercase: true })}.`);
      }
      return stockPickerEmptyMessage(this.selectedStock, 'No stock held at the supplying location.');
    },
  },
  watch: {
    imprest_pom: {
      handler(newVal) {
        if (newVal === '') {
          if (this.defaultApplied) return;
          const movementDefault = this.$store.state.currentLocation?.facility?.medication_movement_default;
          if (movementDefault === 1) {
            this.imprest_pom = 'imprest';
            this.defaultApplied = true;
            this.searchDrugs(true);
          } else if (movementDefault === 2) {
            this.imprest_pom = 'pom';
            this.defaultApplied = true;
          }
        }
      },
      immediate: true
    },
    // A requisition item is a StockItem uuid, which belongs to exactly one register. Changing the
    // source invalidates every row already added, so the list is cleared rather than silently
    // posting uuids the new source does not own.
    sourceLocation(newVal, oldVal) {
      if (oldVal && newVal && oldVal.uuid === newVal.uuid) return;
      this.drugList = [];
      this.selectedStock = null;
      if (this.requestedDrugs.length) {
        this.requestedDrugs = [];
        this.errorMessage = 'The source location changed, so the requested medications were cleared. Please add them again from the new location.';
      }
      if (oldVal && this.imprest_pom === 'imprest') {
        this.searchDrugs(true);
      }
    }
  },
  methods: {
    closeModal() {
      this.$emit('close', true);
    },
    closePatientSearch() {
      this.displayPatientSearchModal = false;
    },
    getPatient(val) {
      this.patient = val;
      this.displayPatientSearchModal = false;
      this.loadPatientMeds();
    },
    loadPatientMeds() {
      if (!this.patient || !this.sourceLocation) return;
      this.loadingDrugs = true;
      axios.post('/get-stock-items', {
        drug: '',
        patient_id: this.patient.uuid,
        location_id: this.sourceLocation.uuid,
      }, this.$store.state.header).then(res => {
        this.drugList = decorateStockItems(res.data, { disableZeroStock: false });
        this.loadingDrugs = false;
      });
    },
    // Location lookups are per side. The counterpart's current selection is excluded so a
    // requisition from a location to itself cannot be built.
    fetchLocations(side, term) {
      const loadingKey = side === 'source' ? 'loadingSource' : 'loadingDestination';
      const optionsKey = side === 'source' ? 'sourceOptions' : 'destinationOptions';
      const otherLocation = side === 'source' ? this.destinationLocation : this.sourceLocation;

      this[loadingKey] = true;
      axios.post('/get-locations', { location: term || '' }, this.$store.state.header).then(res => {
        this[optionsKey] = res.data.locations.filter(loc => !otherLocation || loc.uuid !== otherLocation.uuid);
        this[loadingKey] = false;
      }).catch(() => {
        this[loadingKey] = false;
      });
    },
    loadSourceLocations() {
      this.fetchLocations('source', '');
    },
    loadDestinationLocations() {
      this.fetchLocations('destination', '');
    },
    // Two separate debounce closures - a single shared one would let typing in one field cancel
    // the other field's in-flight search and land the response on the wrong side.
    debouncedSourceSearch: debounce(function() {
      this.fetchLocations('source', pickerSearchTerm(this.sourceSearch));
    }, 500),
    debouncedDestinationSearch: debounce(function() {
      this.fetchLocations('destination', pickerSearchTerm(this.destinationSearch));
    }, 500),
    // The dropdowns are editable, so the model degrades to a plain string once the user types
    // after selecting. Snapshot the selected object here and use that at submit time.
    // Selecting from the list hands the model the whole option object, which an editable Dropdown
    // cannot resolve to a label - it rendered "[object Object]". Put the display string back in the
    // model for the same reason applyLocationDefaults assigns current.name; the record itself lives
    // in sourceLocation / destinationLocation, which is what the payload reads.
    sourceSelect() {
      if (this.sourceSearch && typeof this.sourceSearch === 'object') {
        this.sourceLocation = this.sourceSearch;
        this.sourceSearch = this.sourceLocation.name;
        this.displayCrossLocationWarning = false;
        this.crossLocationAcknowledged = false;
      }
    },
    destinationSelect() {
      if (this.destinationSearch && typeof this.destinationSearch === 'object') {
        this.destinationLocation = this.destinationSearch;
        this.destinationSearch = this.destinationLocation.name;
        this.displayCrossLocationWarning = false;
        this.crossLocationAcknowledged = false;
      }
    },
    // Stock flows pharmacy -> ward, so whichever end the user is standing at is pre-filled.
    //
    // Keyed on the location as well as the role: a pharmacy is staffed by technicians and admins
    // as well as pharmacists, and keying on user.type alone gave all of them the ward default.
    // The location is the stronger signal - it is the end the requisition is actually being
    // raised from - with the role as a fallback for sites that do not type their locations.
    isAtSupplyingLocation() {
      const locationType = this.$store.state.currentLocation?.type;
      if (locationType) {
        return String(locationType).toLowerCase() === 'pharmacy';
      }
      return this.$store.state.user?.type === 'pharmacist';
    },
    applyLocationDefaults() {
      const current = this.$store.state.currentLocation;
      if (!current || !current.uuid) return;
      // The search model gets the *name*, not the location object. These dropdowns are editable,
      // and an editable Dropdown resolves its display label by finding the model value among its
      // `options`. The current location is not in that list - the list has not been fetched yet,
      // and it would be a different object instance even once it has - so passing the object made
      // the field render "[object Object]". The authoritative selection lives in
      // sourceLocation / destinationLocation, which is what the payload reads.
      if (this.isAtSupplyingLocation()) {
        this.sourceLocation = current;
        this.sourceSearch = current.name;
      } else {
        this.destinationLocation = current;
        this.destinationSearch = current.name;
      }
    },
    // The editable dropdowns let the user type over a selection without picking a new one from the
    // list. In that state the search box and the snapshotted location disagree, and submitting
    // would silently move stock to whatever was selected before the typing. Treat that as
    // unselected rather than trusting the stale snapshot.
    locationSelectionIsStale(searchModel, selected) {
      if (!selected) return true;
      if (searchModel && typeof searchModel === 'object') return false;
      const typed = String(searchModel == null ? '' : searchModel).trim().toLowerCase();
      return typed !== String(selected.name || '').trim().toLowerCase();
    },
    searchDrugs: debounce(function(fetchDefault = false) {
      if (!this.sourceLocation) return;

      if (this.imprest_pom === 'pom' && this.patient) {
        // Patient meds are already loaded - filteredDrugList does the filtering.
        return;
      }

      // Only typed text is a search; a held selection has nothing left to look up.
      const searchTerm = pickerSearchTerm(this.selectedStock);
      // Compared against true, not just truthy: this is bound straight to @keyup, so a real
      // keystroke arrives here as the event object rather than a preload flag.
      if (!searchTerm && fetchDefault !== true) return;
      this.loadingDrugs = true;

      // location_id is the whole point of this call: without it the backend falls back to the
      // Location header, which is the user's own location. For a nurse that is the destination,
      // so the list showed what the ward already held rather than what the pharmacy can supply.
      axios.post('/get-stock-items', {
        drug: searchTerm || '',
        patient_id: null,
        location_id: this.sourceLocation.uuid,
      }, this.$store.state.header).then(res => {
        // Nothing is disabled on a zero quantity: a location that has ever held a drug keeps a
        // stock item at quantity 0, and requesting one is the legitimate "stock is arriving" case.
        this.drugList = decorateStockItems(res.data, { disableZeroStock: false });
        this.loadingDrugs = false;
        // Opened on every response, not just the focus preload: an empty result is what the
        // picker most needs to report, and the message that says so only renders while the
        // overlay is up.
        this.show();
      });
    }, 500),
    onDrugFocus() {
      if (this.imprest_pom === 'imprest') {
        if (isPickerEmpty(this.selectedStock)) {
          this.searchDrugs(true);
          this.show();
        }
      }
    },
    show() {
      let attempts = 0;
      if (this.showInterval) clearInterval(this.showInterval);
      this.showInterval = setInterval(() => {
        attempts++;
        if ((!this.loadingDrugs && this.drugList) || attempts > 50) {
          clearInterval(this.showInterval);
          this.showInterval = null;
          if (this.$refs.med && attempts <= 50) {
            this.$refs.med.show();
          }
        }
      }, 100);
    },
    getPersonName(person) {
      if (!person) return '';
      const pi = person.personal_information;
      if (!pi) {
        const first = person.first_name || person.firstname || '';
        const last = person.last_name || person.lastname || '';
        return `${first} ${last}`.trim() || 'Unknown';
      }
      const first = pi.firstname || pi.first_name || '';
      const last = pi.lastname || pi.last_name || '';
      return `${first} ${last}`.trim() || 'Unknown';
    },
    addRequestedDrug() {
      if (!this.selectedStock || typeof this.selectedStock !== 'object' || !this.qty) {
        this.errorMessage = 'Please select a medication from the list and enter a quantity.';
        return;
      }

      const drug = this.selectedStock;

      // stock_item_uuid must be a StockItem uuid from the source register. There is deliberately
      // no alias-id fallback here: that put an alias id into a field the backend reads as a stock
      // item uuid, and the source-register check now rejects it outright.
      if (!drug.uuid) {
        this.errorMessage = 'That medication has no stock record at the source location. Please pick one from the list.';
        return;
      }

      const drugName = drug.name || (drug.alias ? drug.alias.name : '');

      this.requestedDrugs.push({
        stock: drugName,
        stock_item_uuid: drug.uuid,
        qty: Number(this.qty),
        qty_left: drug.qty_left,
        // Carried through to submitPayload.drugs purely so SignatureModal's dualForMeds can see
        // it - see the comment at submitPayload for why that matters.
        tags: (drug.alias && drug.alias.tags) || [],
        patient: this.imprest_pom === 'pom' ? this.patient : null,
      });

      this.selectedStock = null;
      this.qty = null;
      this.errorMessage = '';
      // imprest_pom is deliberately not reset - the requisition is now locked to imprest or
      // resident medication, because the backend rejects a requisition mixing the two. The
      // patient is cleared so the next resident item can be for a different resident.
      if (this.imprest_pom === 'pom') {
        this.patient = null;
      }
    },
    removeDrug(idx) {
      this.requestedDrugs.splice(idx, 1);
    },
    // The real (undebounced) per-item search - see makeDebouncedSupplyGpSearch for why each
    // supply row needs its own debounce closure rather than sharing one at the methods level.
    supplyGpSearch(item) {
      if (!item.gpSearchQuery || item.gpSearchQuery.length < 3) return;
      axios.post('/onload-prescriber', { prescriber: item.gpSearchQuery }, this.$store.state.header).then(res => {
        item.prescriberResults = res.data.prescribers;
      });
    },
    // supplyItems is a plain array of rows (not child components), each with its own
    // prescriber-search input. A single `debounce(fn, 500)` shared across all rows (as this
    // used to be) means every row's search shares one timer/pending call - typing in row 2
    // within 500ms of row 1 cancels row 1's search and the response can land on the wrong
    // row. Each supply item gets its own debounced closure instead, created once in mounted().
    makeDebouncedSupplyGpSearch(item) {
      return debounce(() => this.supplyGpSearch(item), 500);
    },
    selectSupplyPrescriber(item, gp) {
      item.selectedPrescriber = gp;
      item.gpSearchQuery = '';
      item.prescriberResults = [];
    },
    closeSignature() {
      this.displaySignatureModal = false;
    },
    isSuccessful(val) {
      if (val) {
        this.displaySignatureModal = false;
        this.$emit('close', true, 'success');
        if (this.$route.name === 'Requisitions') {
          this.$store.dispatch('getRequisitions', {
            sort_direction: 'desc',
            col: 'created_at',
            page: 1,
            filters: {
              status: ['pending', 'pickup ready', 'transit', 'complete'],
              location: '',
              date: { start: '', end: '' }
            }
          });
        }
      }
    },
    confirmCrossLocation() {
      this.crossLocationAcknowledged = true;
      this.displayCrossLocationWarning = false;
      this.submitRequisition();
    },
    submitRequisition() {
      this.errorMessage = '';

      if (this.mode === 'Create') {
        if (!this.sourceLocation || !this.sourceLocation.uuid || this.locationSelectionIsStale(this.sourceSearch, this.sourceLocation)) {
          this.errorMessage = 'Please select a source location to request stock from, choosing it from the list.';
          return;
        }
        if (!this.destinationLocation || !this.destinationLocation.uuid || this.locationSelectionIsStale(this.destinationSearch, this.destinationLocation)) {
          this.errorMessage = 'Please select a destination location to deliver stock to, choosing it from the list.';
          return;
        }
        if (this.sourceLocation.uuid === this.destinationLocation.uuid) {
          this.errorMessage = 'The source and destination locations must be different.';
          return;
        }
        if (this.requestedDrugs.length === 0) {
          this.errorMessage = 'Please add at least one medication.';
          return;
        }

        // A requisition between two locations the user is not standing at is allowed, but it will
        // not show up in their own requisition list, so make that explicit before they sign.
        const currentUuid = this.$store.state.currentLocation?.uuid;
        const isCrossLocation = this.sourceLocation.uuid !== currentUuid && this.destinationLocation.uuid !== currentUuid;
        if (isCrossLocation && !this.crossLocationAcknowledged) {
          this.displayCrossLocationWarning = true;
          return;
        }

        const requisitionItems = this.requestedDrugs.map(item => ({
          stock_item_uuid: item.stock_item_uuid,
          qty: item.qty,
          patient_uuid: item.patient ? item.patient.uuid : null,
        }));

        // Stock always flows from the supplying location to the requesting location: the
        // requisition movement decrements `from` and lands the stock at `to` (see
        // StockMovementModels\Requisition - pickupRequisition moves from->to and
        // completeRequisition writes it into `to` as stock).
        //
        // fromLocation/toLocation are deliberately NOT sent: they are the keys
        // CreateRequisitionRequest's LocationExistsRule injects the resolved models into, and the
        // client must never pre-populate a resolved-model key.
        this.submitPayload = {
          from: this.sourceLocation.uuid,
          to: this.destinationLocation.uuid,
          requisition_items: requisitionItems,
          notes: this.notes,
          // CreateRequisitionRequest has no validation rule for `drugs`, so the backend ignores
          // it - it exists purely so SignatureModal's dualForMeds computed (which reads
          // payload.drugs[].tags, see services/doubleSignature.js) can see whether an S8 (or
          // facility-nominated) drug is on this requisition and require two signatures.
          // Without it dualForMeds has nothing to read and always falls back to a single
          // signature, regardless of what is actually being requisitioned.
          drugs: this.requestedDrugs.map(d => ({ tags: d.tags })),
        };

        this.saveContext = {
          type: 'Requisition Create',
          location: `${this.sourceLocation.name} → ${this.destinationLocation.name}`,
          drugs: this.requestedDrugs.map(d => ({ drug: d.stock, qty: d.qty })),
          doubleSig: true,
        };

        this.displaySignatureModal = true;
      }

      else {
        // Supply Mode - the requisition is supplied as requested. There is no per-item selection:
        // every unit on the requisition is sent, and the backend rejects a payload that does not
        // cover all of them.
        if (this.supplyItems.length === 0) {
          this.errorMessage = 'This requisition has no items to supply.';
          return;
        }

        const items = this.supplyItems.map(item => ({
          unit_id: item.uuid,
          patient_id: item.patient ? item.patient.uuid : null,
          script_number: item.script_number || null,
          prescriber_id: item.selectedPrescriber ? item.selectedPrescriber.uuid : null,
        }));

        // dispensingType is deliberately NOT sent. DistributeRequisitionRule derives it from the
        // requisition's own units and merges it into the request, so anything the client sends is
        // overwritten - and a client must never pre-populate a key the server injects (same rule
        // as fromLocation/toLocation on the create payload).
        this.submitPayload = {
          requisition_id: this.requisitionData.uuid,
          requisition_items: items,
          // Same reasoning as the Create-mode payload above: SupplyRequisitionRequest ignores
          // this key, it exists only so dualForMeds sees the tags and asks for two signatures on
          // an S8 supply. item.alias.tags is already present on each supplyItem - RequisitionItemResource
          // eager-loads units.alias.tags server-side.
          drugs: this.supplyItems.map(item => ({ tags: (item.alias && item.alias.tags) || [] })),
        };

        this.saveContext = {
          type: 'Requisition Supply',
          location: this.requisitionData.to ? this.requisitionData.to.name : 'Requesting Location',
          drugs: this.supplyItems.map(item => ({ drug: item.alias ? item.alias.name : 'Unknown', qty: item.quantity })),
          doubleSig: true,
        };

        this.displaySignatureModal = true;
      }
    }
  },
  mounted() {
    if (this.mode === 'Supply' && this.requisitionData) {
      this.supplyItems = this.requisitionData.requisition_items.map(item => {
        const supplyItem = {
          ...item,
          script_number: item.script_number || '',
          gpSearchQuery: '',
          prescriberResults: [],
          selectedPrescriber: item.prescriber || null,
        };
        supplyItem.debouncedGpSearch = this.makeDebouncedSupplyGpSearch(supplyItem);
        return supplyItem;
      });
    } else {
      this.applyLocationDefaults();
    }
  },
  beforeUnmount() {
    if (this.showInterval) {
      clearInterval(this.showInterval);
    }
  }
}
</script>

<style lang="scss" scoped>
@import "../../assets/css/main.scss";

.modal-body {
  padding: 20px;
}

.right-form-section-drug-entry {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;

  input {
    background-color: $babyblue;
  }
}

.right-form-section-drug-container {
  height: 200px;
  overflow-y: auto;
}

.right-form-section-panel {
  padding: 0 20px;
}

.drug-entry-transfer {
  border-collapse: collapse;

  p {
    margin: 0;
    text-align: left;
    padding: 0 15px;
  }

  background-color: $grey;
  border-radius: 4px;
  margin-bottom: 10px;
  padding: 5px 10px;
  border-bottom: 5px solid white;
}
</style>
